/**
 * Column Normalizer
 * =================
 * Handle varying column names across CSV files.
 * Maps common variations to canonical field names.
 */

// Canonical field names used internally
export const CANONICAL_FIELDS = {
  RANK: 'rank',
  TIME: 'time',
  STATUS: 'status',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  BIB: 'bib',
  FIS_CODE: 'fisCode',
  USSA_CODE: 'ussaCode',
  FIS_POINTS: 'fisPoints',
  USSA_POINTS: 'ussaPoints',
  COUNTRY: 'country',
  YOB: 'yob',
  CLUB: 'club',
  CATEGORY: 'category',
  GENDER: 'gender',
  AGE_CLASS: 'ageClass',
  // Para fields
  GUIDE_NAME: 'guideName',
  GUIDE_FIS: 'guideFis',
  // Sprint heats fields
  HIGHEST_LEVEL: 'highestLevel',
  QUARTER_RANK: 'quarterRank',
  QUARTER_TIME: 'quarterTime',
  SEMI_RANK: 'semiRank',
  SEMI_TIME: 'semiTime',
};

// Map of source column names → canonical field
// Must match all variants from v1 CSVParser column mappings
const COLUMN_MAP = {
  // Rank
  rank: CANONICAL_FIELDS.RANK,
  Rank: CANONICAL_FIELDS.RANK,
  RANK: CANONICAL_FIELDS.RANK,
  '(rank)': CANONICAL_FIELDS.RANK,
  place: CANONICAL_FIELDS.RANK,
  Place: CANONICAL_FIELDS.RANK,
  position: CANONICAL_FIELDS.RANK,
  Position: CANONICAL_FIELDS.RANK,
  
  // Time
  time: CANONICAL_FIELDS.TIME,
  Time: CANONICAL_FIELDS.TIME,
  TIME: CANONICAL_FIELDS.TIME,
  Finish: CANONICAL_FIELDS.TIME,
  finish: CANONICAL_FIELDS.TIME,
  'finish time': CANONICAL_FIELDS.TIME,
  'Finish Time': CANONICAL_FIELDS.TIME,
  'final time': CANONICAL_FIELDS.TIME,
  'Final Time': CANONICAL_FIELDS.TIME,
  'Total Time': CANONICAL_FIELDS.TIME,
  totalTime: CANONICAL_FIELDS.TIME,
  TotalTime: CANONICAL_FIELDS.TIME,
  
  // Status
  status: CANONICAL_FIELDS.STATUS,
  Status: CANONICAL_FIELDS.STATUS,
  STATUS: CANONICAL_FIELDS.STATUS,
  'result status': CANONICAL_FIELDS.STATUS,
  'finish status': CANONICAL_FIELDS.STATUS,
  
  // First Name
  firstname: CANONICAL_FIELDS.FIRST_NAME,
  firstName: CANONICAL_FIELDS.FIRST_NAME,
  FirstName: CANONICAL_FIELDS.FIRST_NAME,
  first_name: CANONICAL_FIELDS.FIRST_NAME,
  'First Name': CANONICAL_FIELDS.FIRST_NAME,
  'first name': CANONICAL_FIELDS.FIRST_NAME,
  'given name': CANONICAL_FIELDS.FIRST_NAME,
  FIRSTNAME: CANONICAL_FIELDS.FIRST_NAME,
  
  // Last Name
  lastname: CANONICAL_FIELDS.LAST_NAME,
  lastName: CANONICAL_FIELDS.LAST_NAME,
  LastName: CANONICAL_FIELDS.LAST_NAME,
  last_name: CANONICAL_FIELDS.LAST_NAME,
  'Last Name': CANONICAL_FIELDS.LAST_NAME,
  'last name': CANONICAL_FIELDS.LAST_NAME,
  surname: CANONICAL_FIELDS.LAST_NAME,
  'family name': CANONICAL_FIELDS.LAST_NAME,
  LASTNAME: CANONICAL_FIELDS.LAST_NAME,
  
  // Bib
  bib: CANONICAL_FIELDS.BIB,
  Bib: CANONICAL_FIELDS.BIB,
  BIB: CANONICAL_FIELDS.BIB,
  'Racer #': CANONICAL_FIELDS.BIB,
  'racer number': CANONICAL_FIELDS.BIB,
  'bib number': CANONICAL_FIELDS.BIB,
  num: CANONICAL_FIELDS.BIB,
  Round: CANONICAL_FIELDS.BIB,
  
  // FIS Code - comprehensive list from v1
  FIScode: CANONICAL_FIELDS.FIS_CODE,
  fisCode: CANONICAL_FIELDS.FIS_CODE,
  'FIS Licence': CANONICAL_FIELDS.FIS_CODE,
  FISLicence: CANONICAL_FIELDS.FIS_CODE,
  'FIS License': CANONICAL_FIELDS.FIS_CODE,
  FISLicense: CANONICAL_FIELDS.FIS_CODE,
  'FIS Code': CANONICAL_FIELDS.FIS_CODE,
  FISCode: CANONICAL_FIELDS.FIS_CODE,
  'FIS #': CANONICAL_FIELDS.FIS_CODE,
  FIS: CANONICAL_FIELDS.FIS_CODE,
  fis_licence: CANONICAL_FIELDS.FIS_CODE,
  fis_license: CANONICAL_FIELDS.FIS_CODE,
  'FIS Racing License': CANONICAL_FIELDS.FIS_CODE,
  'FIS Racing Licence': CANONICAL_FIELDS.FIS_CODE,
  FISRacingLicense: CANONICAL_FIELDS.FIS_CODE,
  FISRacingLicence: CANONICAL_FIELDS.FIS_CODE,
  
  // USSA Code - comprehensive list from v1
  USSAcode: CANONICAL_FIELDS.USSA_CODE,
  ussaCode: CANONICAL_FIELDS.USSA_CODE,
  USSANumber: CANONICAL_FIELDS.USSA_CODE,
  ussaNumber: CANONICAL_FIELDS.USSA_CODE,
  'USSA Number': CANONICAL_FIELDS.USSA_CODE,
  'USSA Code': CANONICAL_FIELDS.USSA_CODE,
  'USSA #': CANONICAL_FIELDS.USSA_CODE,
  USSA: CANONICAL_FIELDS.USSA_CODE,
  'USSA Racing License': CANONICAL_FIELDS.USSA_CODE,
  USSARacingLicense: CANONICAL_FIELDS.USSA_CODE,
  'USSA Racing Licence': CANONICAL_FIELDS.USSA_CODE,
  USSARacingLicence: CANONICAL_FIELDS.USSA_CODE,
  'USSA License': CANONICAL_FIELDS.USSA_CODE,
  USSALicense: CANONICAL_FIELDS.USSA_CODE,
  ussa_number: CANONICAL_FIELDS.USSA_CODE,
  'USSS #': CANONICAL_FIELDS.USSA_CODE,
  USSS: CANONICAL_FIELDS.USSA_CODE,
  'USSS Number': CANONICAL_FIELDS.USSA_CODE,
  'USSSA Number': CANONICAL_FIELDS.USSA_CODE,
  'USSSA #': CANONICAL_FIELDS.USSA_CODE,
  USSSA: CANONICAL_FIELDS.USSA_CODE,
  USSSANumber: CANONICAL_FIELDS.USSA_CODE,
  'NC License': CANONICAL_FIELDS.USSA_CODE,
  'NC Licence': CANONICAL_FIELDS.USSA_CODE,
  NCLicense: CANONICAL_FIELDS.USSA_CODE,
  NCLicence: CANONICAL_FIELDS.USSA_CODE,
  
  // FIS Points - comprehensive list from v1
  FISpoints: CANONICAL_FIELDS.FIS_POINTS,
  fisPoints: CANONICAL_FIELDS.FIS_POINTS,
  'FIS Points': CANONICAL_FIELDS.FIS_POINTS,
  'FIS Distance': CANONICAL_FIELDS.FIS_POINTS,
  'FIS (DI or SP) Points': CANONICAL_FIELDS.FIS_POINTS,
  FISPoints: CANONICAL_FIELDS.FIS_POINTS,
  fis_points: CANONICAL_FIELDS.FIS_POINTS,
  
  // USSA Points - comprehensive list from v1
  NRLpoints: CANONICAL_FIELDS.USSA_POINTS,
  nrlPoints: CANONICAL_FIELDS.USSA_POINTS,
  'NRL Points': CANONICAL_FIELDS.USSA_POINTS,
  'USSA Distance': CANONICAL_FIELDS.USSA_POINTS,
  USSADistance: CANONICAL_FIELDS.USSA_POINTS,
  'USSA Points': CANONICAL_FIELDS.USSA_POINTS,
  USSAPoints: CANONICAL_FIELDS.USSA_POINTS,
  ussaPoints: CANONICAL_FIELDS.USSA_POINTS,
  
  // Country
  Country: CANONICAL_FIELDS.COUNTRY,
  country: CANONICAL_FIELDS.COUNTRY,
  COUNTRY: CANONICAL_FIELDS.COUNTRY,
  Nation: CANONICAL_FIELDS.COUNTRY,
  nation: CANONICAL_FIELDS.COUNTRY,
  NAT: CANONICAL_FIELDS.COUNTRY,
  Nat: CANONICAL_FIELDS.COUNTRY,
  nationcode: CANONICAL_FIELDS.COUNTRY,
  
  // Year of Birth
  YOB: CANONICAL_FIELDS.YOB,
  yob: CANONICAL_FIELDS.YOB,
  'Birth Year': CANONICAL_FIELDS.YOB,
  BirthYear: CANONICAL_FIELDS.YOB,
  'Year of Birth': CANONICAL_FIELDS.YOB,
  YearOfBirth: CANONICAL_FIELDS.YOB,
  year_of_birth: CANONICAL_FIELDS.YOB,
  
  // Club
  Club: CANONICAL_FIELDS.CLUB,
  club: CANONICAL_FIELDS.CLUB,
  CLUB: CANONICAL_FIELDS.CLUB,
  Team: CANONICAL_FIELDS.CLUB,
  team: CANONICAL_FIELDS.CLUB,
  
  // Category / Age Class
  Category: CANONICAL_FIELDS.CATEGORY,
  category: CANONICAL_FIELDS.CATEGORY,
  CATEGORY: CANONICAL_FIELDS.CATEGORY,
  'Age Class': CANONICAL_FIELDS.AGE_CLASS,
  ageClass: CANONICAL_FIELDS.AGE_CLASS,
  
  // Gender
  Gender: CANONICAL_FIELDS.GENDER,
  gender: CANONICAL_FIELDS.GENDER,
  sex: CANONICAL_FIELDS.GENDER,
  Sex: CANONICAL_FIELDS.GENDER,
  
  // Para fields - comprehensive list from v1
  guideName: CANONICAL_FIELDS.GUIDE_NAME,
  guide_name: CANONICAL_FIELDS.GUIDE_NAME,
  GuideName: CANONICAL_FIELDS.GUIDE_NAME,
  Guide: CANONICAL_FIELDS.GUIDE_NAME,
  'Guide Name': CANONICAL_FIELDS.GUIDE_NAME,
  guideFis: CANONICAL_FIELDS.GUIDE_FIS,
  guide_fis: CANONICAL_FIELDS.GUIDE_FIS,
  GuideFIS: CANONICAL_FIELDS.GUIDE_FIS,
  guideFIScode: CANONICAL_FIELDS.GUIDE_FIS,
  'Guide FIS Licence': CANONICAL_FIELDS.GUIDE_FIS,
  'Guide FIS License': CANONICAL_FIELDS.GUIDE_FIS,
  'Guide Fiscode': CANONICAL_FIELDS.GUIDE_FIS,
  
  // Sprint heats fields
  'Highest Level': CANONICAL_FIELDS.HIGHEST_LEVEL,
  highestLevel: CANONICAL_FIELDS.HIGHEST_LEVEL,
  HighestLevel: CANONICAL_FIELDS.HIGHEST_LEVEL,
  'highest level': CANONICAL_FIELDS.HIGHEST_LEVEL,
  Level: CANONICAL_FIELDS.HIGHEST_LEVEL,
  level: CANONICAL_FIELDS.HIGHEST_LEVEL,
  'Quarter Rank': CANONICAL_FIELDS.QUARTER_RANK,
  quarterRank: CANONICAL_FIELDS.QUARTER_RANK,
  QuarterRank: CANONICAL_FIELDS.QUARTER_RANK,
  'Quarter Time': CANONICAL_FIELDS.QUARTER_TIME,
  quarterTime: CANONICAL_FIELDS.QUARTER_TIME,
  QuarterTime: CANONICAL_FIELDS.QUARTER_TIME,
  'Semi Rank': CANONICAL_FIELDS.SEMI_RANK,
  semiRank: CANONICAL_FIELDS.SEMI_RANK,
  SemiRank: CANONICAL_FIELDS.SEMI_RANK,
  'Semi Time': CANONICAL_FIELDS.SEMI_TIME,
  semiTime: CANONICAL_FIELDS.SEMI_TIME,
  SemiTime: CANONICAL_FIELDS.SEMI_TIME,
};

/**
 * Normalize a single column name to canonical form.
 * @param {string} columnName - Original column name
 * @returns {string} Canonical field name or original if not mapped
 */
export function normalizeColumnName(columnName) {
  return COLUMN_MAP[columnName] || columnName;
}

/**
 * Normalize all keys in a racer object.
 * @param {Object} racer - Raw racer object from CSV
 * @returns {Object} Normalized racer object
 */
export function normalizeRacer(racer) {
  const normalized = {};
  for (const [key, value] of Object.entries(racer)) {
    const normalizedKey = normalizeColumnName(key);
    normalized[normalizedKey] = value;
  }
  return normalized;
}

/**
 * Normalize an array of racers.
 * @param {Array<Object>} racers - Raw racer objects
 * @returns {Array<Object>} Normalized racers
 */
export function normalizeRacers(racers) {
  return racers.map(normalizeRacer);
}

/**
 * Get the original column name from a header array that maps to a canonical field.
 * @param {Array<string>} headers - Original headers
 * @param {string} canonicalField - Canonical field to find
 * @returns {string|null} Original column name or null
 */
export function findOriginalColumn(headers, canonicalField) {
  for (const header of headers) {
    if (normalizeColumnName(header) === canonicalField) {
      return header;
    }
  }
  return null;
}

/**
 * Check if racer data has a specific canonical field populated.
 * @param {Object} racer - Racer object (original or normalized)
 * @param {string} canonicalField - Field to check
 * @returns {boolean} True if field exists and is non-empty
 */
export function hasField(racer, canonicalField) {
  // Check normalized form
  if (racer[canonicalField] && racer[canonicalField] !== '') {
    return true;
  }
  
  // Check original form variations
  for (const [original, canonical] of Object.entries(COLUMN_MAP)) {
    if (canonical === canonicalField && racer[original] && racer[original] !== '') {
      return true;
    }
  }
  
  return false;
}

/**
 * Get a field value from a racer, trying both normalized and original names.
 * @param {Object} racer - Racer object
 * @param {string} canonicalField - Field to get
 * @returns {*} Field value or undefined
 */
export function getField(racer, canonicalField) {
  // Try normalized form first
  if (racer[canonicalField] !== undefined) {
    return racer[canonicalField];
  }
  
  // Try original form variations
  for (const [original, canonical] of Object.entries(COLUMN_MAP)) {
    if (canonical === canonicalField && racer[original] !== undefined) {
      return racer[original];
    }
  }
  
  return undefined;
}

/**
 * Check if any racer in the data has a value for a given field.
 * @param {Array} racers - Array of racer objects
 * @param {string} canonicalField - Canonical field name to check
 * @returns {boolean} True if at least one racer has this field
 */
export function dataHasField(racers, canonicalField) {
  if (!racers || !Array.isArray(racers) || racers.length === 0) return false;
  return racers.some(racer => {
    const val = getField(racer, canonicalField);
    return val !== undefined && val !== null && val !== '';
  });
}

/**
 * Get all column names from the first racer (original CSV headers).
 * @param {Array} racers - Array of racer objects
 * @returns {string[]} Array of column names
 */
export function getAvailableColumns(racers) {
  if (!racers || racers.length === 0) return [];
  return Object.keys(racers[0]);
}

/**
 * Detect missing required fields for a race type.
 * @param {Array} racers - Array of racer objects  
 * @param {Object} options - { isFIS: boolean, isUSSA: boolean }
 * @returns {Object} { missingFields: string[], availableColumns: string[] }
 */
export function detectMissingFields(racers, { isFIS = false, isUSSA = false } = {}) {
  const missingFields = [];
  const availableColumns = getAvailableColumns(racers);
  
  // Check for FIS code if this is a FIS race
  if (isFIS && !dataHasField(racers, CANONICAL_FIELDS.FIS_CODE)) {
    missingFields.push({
      field: CANONICAL_FIELDS.FIS_CODE,
      label: 'FIS License/Code',
      required: true,
    });
  }
  
  // Check for USSA code if this is a USSA race
  if (isUSSA && !dataHasField(racers, CANONICAL_FIELDS.USSA_CODE)) {
    missingFields.push({
      field: CANONICAL_FIELDS.USSA_CODE,
      label: 'USSA License/Code',
      required: true,
    });
  }
  
  return { missingFields, availableColumns };
}

/**
 * Apply manual column mapping to racers.
 * Also copies to common field names expected by v1 XML generators.
 * @param {Array} racers - Array of racer objects
 * @param {Object} mappings - { canonicalField: sourceColumn }
 * @returns {Array} Updated racers with mapped fields
 */
export function applyColumnMappings(racers, mappings) {
  if (!mappings || Object.keys(mappings).length === 0) return racers;
  
  // Map canonical fields to common v1 field names that XML generators expect
  const v1FieldNames = {
    [CANONICAL_FIELDS.FIS_CODE]: ['FIScode', 'fisCode', 'FIS License', 'FIS Licence'],
    [CANONICAL_FIELDS.USSA_CODE]: ['USSAcode', 'ussaCode', 'USSA Number'],
    [CANONICAL_FIELDS.FIS_POINTS]: ['FISpoints', 'fisPoints'],
    [CANONICAL_FIELDS.USSA_POINTS]: ['NRLpoints', 'ussaPoints'],
    [CANONICAL_FIELDS.FIRST_NAME]: ['firstname', 'FirstName'],
    [CANONICAL_FIELDS.LAST_NAME]: ['lastname', 'LastName'],
    [CANONICAL_FIELDS.COUNTRY]: ['Country', 'nation'],
    [CANONICAL_FIELDS.YOB]: ['YOB', 'yob'],
  };
  
  return racers.map(racer => {
    const updated = { ...racer };
    for (const [canonicalField, sourceColumn] of Object.entries(mappings)) {
      if (sourceColumn && racer[sourceColumn] !== undefined) {
        const value = racer[sourceColumn];
        // Set canonical field
        updated[canonicalField] = value;
        // Also set common v1 field names for this field type
        const v1Names = v1FieldNames[canonicalField];
        if (v1Names) {
          for (const v1Name of v1Names) {
            updated[v1Name] = value;
          }
        }
      }
    }
    return updated;
  });
}

const columnNormalizer = {
  CANONICAL_FIELDS,
  normalizeColumnName,
  normalizeRacer,
  normalizeRacers,
  findOriginalColumn,
  hasField,
  getField,
  dataHasField,
  getAvailableColumns,
  detectMissingFields,
  applyColumnMappings,
};

export default columnNormalizer;
